<template>
  <v-row>
    <v-col cols="12" v-if="$store.state.invoices.retrieveLoader">
      <v-skeleton-template type="table-heading, list-item-two-line" />
      <v-skeleton-loader class="mt-5" transition="fade-transition" type="table" />
    </v-col>
    <v-col cols="12" v-else>
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">
            <v-row align="center" justify="start" no-gutters>
              <router-link class="subtitle-1 routerLink font-weight-medium" :to="{name: 'InvoiceList'}">Facturas</router-link>
              <v-icon class="mx-1 pt-1" color="secondary">mdi-chevron-right</v-icon>
              <span class="subtitle-1 font-weight-medium d-inline-block text-truncate" :style="$vuetify.breakpoint.width < 1200 ? 'max-width: 250px;' : 'max-width: 300px;'">{{instance.document.name}} para {{instance.customer.name}}</span>
            </v-row>
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row class="mr-1" v-if="instance.status !== 'pending'" align="center" justify="center">
              <v-btn class="ml-1" @click="dialogReminder=true" outlined color="secondary"><v-icon small left>mdi-email</v-icon>Enviar factura</v-btn>
              <v-btn class="ml-1" @click="renderDocument('invoices')" outlined color="secondary" retain-focus-on-click><v-icon small left>mdi-file-document</v-icon>PDF</v-btn>

              <!-- more options -->
              <v-menu close-on-click close-on-content-click offset-y left :nudge-bottom="10" transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="ml-1" v-bind="attrs" v-on="on" retain-focus-on-click outlined color="secondary">Más<v-icon right>mdi-chevron-down</v-icon></v-btn>
                </template>
                <v-list class="pa-0">
                  <v-list-item class="px-5 py-1" @click="cloneInvoice" :ripple="false">
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium"><v-icon color="secondary" small left>mdi-file-replace</v-icon>Duplicar</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-5 py-1" v-if="['finalized', 'uncollectible'].includes(instance.status)" @click="updateCollectible" :ripple="false">
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">
                      <v-icon color="secondary" small left>mdi-hand-back-left-off</v-icon>
                      <template v-if="instance.status === 'finalized'">Marcar como incobrable</template>
                      <template v-else>Marcar como cobrable</template>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-5 py-1" v-if="totalAdjustedInvoice > 0" @click="(invoiceType.partialCreditNotes ? dialogCreditNote=true : dialogCreditNoteTotal=true)" :ripple="false">
                    <v-list-item-title class="subtitle-2 red--text font-weight-medium"><v-icon color="red" small left>mdi-file-document-minus</v-icon>Crear nota de crédito</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-5 py-1" v-if="totalAdjustedInvoice > 0 && instance.document.country === 'MX'" @click="dialogCancellation=true" :ripple="false">
                    <v-list-item-title class="subtitle-2 red--text font-weight-medium"><v-icon color="red" small left>mdi-file-document-remove</v-icon>Crear comprobante de cancelación</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <!-- end more options -->

            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <v-row class="mx-0 background">
            <v-col md="2">
              <span class="d-block mb-1">Total</span>
              <span class="body-1 font-weight-medium mr-1">{{instance.total | currency(instance.currency)}}</span>
              <span>{{instance.currency}}</span>
            </v-col>
            <v-divider class="my-2" vertical />
            <template v-if="totalAdjustedInvoice < instance.total">
              <v-col md="2">
                <span class="d-block mb-1">
                  Total ajustado
                  <v-tooltip right max-width="280px" transition="scale-transition">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                    </template>
                    <span>Total ajustado es el resultado del total de la factura restado por el total de las notas de crédito. Representa el valor real a pagar por la factura.</span>
                  </v-tooltip>
                </span>
                <span class="body-1 font-weight-medium mr-1">{{totalAdjustedInvoice | currency(instance.currency)}}</span>
                <span>{{instance.currency}}</span>
              </v-col>
              <v-divider class="my-2" vertical />
            </template>
            <v-col md="3" class="ml-2">
              <span class="d-block mb-1">Cliente</span>
              <v-btn :to="{name:'CustomerRetrieveUpdate', params:{id: instance.customer.id}}" height="20" text color="primary" retain-focus-on-click :ripple="false">
                <span class="d-inline-block text-truncate" style="max-width: 200px;">{{instance.customer.name}}</span>
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-col>
            <v-divider class="my-2" vertical />
            <v-col md="2" class="ml-2">
              <span class="d-block mb-1">Fecha</span>
              <span class="font-weight-medium mt-1">{{instance.date | date}}</span>
            </v-col>
            <v-divider class="my-2" vertical />
            <v-col class="ml-2" v-if="instance.status !== 'cancelled'">
              <span class="d-block mb-1">Pago</span>
              <v-chip v-if="instance.status === 'finalized' && instance.due_date < today" color="red lighten-5" text-color="red" small>Vencido</v-chip>
              <v-chip v-else-if="instance.status === 'finalized'" color="defaultGrey" text-color="secondary" small>Pendiente</v-chip>
              <v-chip v-else-if="instance.status === 'uncollectible'" color="defaultGrey" text-color="secondary" small>Incobrable</v-chip>
              <template v-else-if="['paid', 'pending'].includes(instance.status)">
                <v-chip color="lightBlue" text-color="primary" small>Pagado<v-icon class="mx-1" x-small>mdi-check</v-icon></v-chip>
                <span class="ml-2">{{instance.payments[instance.payments.length - 1].updated | dateFromNow}}</span>
              </template>
            </v-col>
          </v-row>
          <v-divider />
          <v-list>
            <!-- credit note -->
            <template v-if="creditnotesList.length">
              <template v-for="(c, i) in creditnotesList">
                <v-list-item class="py-1" :key="c.id">
                  <v-list-item-icon><v-icon color="primary">mdi-text-box</v-icon></v-list-item-icon>
                  <v-list-item-title>
                    <v-btn :to="{name: 'CreditNoteRetrieve', params: {id: c.id}}" text color="primary" height="20" :ripple="false">Nota de crédito Nº {{c.number}}</v-btn>
                    <span class="ml-1">por {{c.note}}</span>
                    <span class="d-block">{{c.created | dateTime}}</span>
                  </v-list-item-title>
                </v-list-item>
                <v-divider :key="`credit-note-divider-${i}`"/>
              </template>
            </template>
            <!-- end credit note -->

            <!-- payment status -->
            <template v-if="instance.status != 'cancelled'">
              <v-list-item class="py-1">
                <v-list-item-icon><v-icon color="primary">mdi-credit-card</v-icon></v-list-item-icon>
                <v-list-item-title v-if="instance.collection_method === 'automatically'">
                  <span class="d-block body-2 text--secondary font-weight-medium">La factura fue marcada como pagada ya que el pago fue inmediato</span>
                  <span class="d-block">{{instance.created | date}}</span>
                </v-list-item-title>

                <template v-else-if="['deferred', 'deferred_dues'].includes(instance.collection_method)">
                  <v-list-item-title v-if="instance.status === 'finalized'">
                    <span class="d-block body-2 text--secondary font-weight-medium">La factura está pendiente de pago</span>
                    <span class="d-block">{{instance.created | date}}</span>
                  </v-list-item-title>
                  <v-list-item-title v-else-if="instance.status === 'paid'">
                    <span class="d-block body-2 text--secondary font-weight-medium">La factura está pagada</span>
                    <span class="d-block">{{instance.created | date}}</span>
                  </v-list-item-title>
                  <v-list-item-title v-else-if="instance.status === 'pending'">
                    <span class="d-block body-2 text--secondary font-weight-medium">La factura está pagada pero aún no está completamente creada</span>
                    <span class="d-block">{{instance.created | date}}</span>
                  </v-list-item-title>
                  <v-list-item-title v-else-if="instance.status === 'uncollectible'">
                    <span class="d-block body-2 text--secondary font-weight-medium">La factura se marcó como incobrable</span>
                    <span class="d-block">{{instance.uncollectible | dateTime}}</span>
                  </v-list-item-title>
                </template>
              </v-list-item>
              <v-divider />
            </template>
            <!-- end payment status -->

            <!-- reminders -->
            <template v-if="messagesList.length">
              <template v-for="(d, i) in messagesList">
                <v-list-item class="py-1" :key="i">
                  <v-list-item-icon><v-icon color="primary">mdi-email-mark-as-unread</v-icon></v-list-item-icon>
                  <v-list-item-title>
                    <span class="d-block body-2 text--secondary font-weight-medium">
                      La factura fue enviada a {{d.recipient[0]}}
                      <v-tooltip v-if="d.recipient.length > 1" right min-width="200px" transition="scale-transition">
                        <template v-slot:activator="{on}">
                          <span v-on="on">y otros {{d.recipient.length - 1}} contactos</span>
                        </template>
                        <template v-for="(c, i) in d.recipient">
                          <span class="d-block" v-if="i > 0" :key="i">{{c}}</span>
                        </template>
                      </v-tooltip>
                    </span>
                    <span class="d-block">{{d.created | dateTime}}</span>
                  </v-list-item-title>
                </v-list-item>
                <v-divider :key="`divider-${i}`" />
              </template>
            </template>
            <!-- end reminders -->
          </v-list>
          <v-row>
            <v-col cols="6">
              <v-list class="transparent">
                <v-list-item>
                  <v-list-item-title class="text--secondary subtitle-1 font-weight-medium">Información general</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Tipo de factura</v-list-item-title>
                  <v-list-item-subtitle>{{instance.document.name}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-if="!['draft', 'pending'].includes(instance.status)">
                  <v-list-item-title>Número</v-list-item-title>
                  <v-list-item-subtitle>{{instance.number}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-if="instance.subscription">
                  <v-list-item-title>Suscripción</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-btn :to="{name: 'SubscriptionRetrieve', params: {id: instance.subscription}}" height="20" text color="primary" retain-focus-on-click :ripple="false">{{instance.subscription}}<v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>ID</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-tooltip right transition="scale-transition">
                      <template v-slot:activator="{on}">
                        <v-btn class="font-weight-regular" @click="toClipboard(instance.id)" text color="secondary" v-on="on" :ripple="false">{{instance.id}}</v-btn>
                      </template>
                      <span>Copiar</span>
                    </v-tooltip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Creado el</v-list-item-title>
                  <v-list-item-subtitle>{{instance.created | dateTime}}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="6">
              <v-list class="transparent">
                <v-list-item>
                  <v-list-item-title class="text--secondary subtitle-1 font-weight-medium">Información de cobranza</v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Fecha de vencimiento</v-list-item-title>
                  <v-list-item-subtitle v-if="instance.due_date">{{instance.due_date | date}}</v-list-item-subtitle>
                  <v-list-item-subtitle class="font-italic text--disabled" v-else>Sin fecha de vencimiento</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Período de facturación</v-list-item-title>
                  <v-list-item-subtitle v-if="instance.period_start && instance.period_end">{{instance.period_start | date}} al {{instance.period_end | date}}</v-list-item-subtitle>
                  <v-list-item-subtitle class="font-italic text--disabled" v-else>Sin especificar</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- SII management -->
      <v-card class="px-0 my-5" v-if="account.country.id == 'CL' && instance.document.has_local_format && !['draft', 'pending'].includes(instance.status)">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Gestión Servicio de Impuestos Internos</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-menu close-on-click close-on-content-click offset-y left :nudge-bottom="10" transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" retain-focus-on-click outlined color="secondary">Opciones</v-btn>
                </template>
                <v-list class="pa-0">
                  <v-list-item class="px-5 py-1" v-if="instance.local_file" :href="instance.local_file" target="_blank" :ripple="false">
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">Ver XML</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-5 py-1" v-if="!grant.id && [33, 34].includes(instance.document.code)" @click="dialogGrant=true" :ripple="false">
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">Ceder factura</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="px-0 background">
          <v-list class="v-list-form-extended transparent">
            <template v-if="!instance.imported">
              <v-list-item>
                <v-list-item-title>Estado</v-list-item-title>
                <v-list-item-subtitle>
                  <template v-if="instance.tax_agency.cl_id">
                    <template v-if="instance.tax_agency.cl_status">
                      <v-chip v-if="instance.tax_agency.cl_status.includes('Rechazado')" class="caption" color="defaultGrey" text-color="secondary" small>{{instance.tax_agency.cl_status}}<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
                      <v-chip v-else color="lightBlue" text-color="primary" small>{{instance.tax_agency.cl_status}}<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                    </template>
                    <template v-else>
                      <v-chip color="defaultGrey" text-color="secondary" small>En espera<v-icon class="ml-1" x-small>mdi-clock</v-icon></v-chip>
                    </template>
                  </template>
                  <template v-else>
                    <v-chip color="defaultGrey" text-color="secondary" small>Procesando documento<v-icon class="ml-1" x-small>mdi-cogs</v-icon></v-chip>
                  </template>
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="instance.tax_agency.cl_status_detail">
                <v-list-item-title>Detalle del estado</v-list-item-title>
                <v-list-item-subtitle>{{instance.tax_agency.cl_status_detail}}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="instance.tax_agency.cl_id">
                <v-list-item-title>Track ID</v-list-item-title>
                <v-list-item-subtitle>{{instance.tax_agency.cl_id}}</v-list-item-subtitle>
              </v-list-item>
            </template>
            <v-list-item>
              <v-list-item-title>Referencias</v-list-item-title>
              <v-list-item-subtitle>
                <template v-if="instance.references.length">
                  <span class="d-block" v-for="d in instance.references" :key="d.document">• Documento tipo {{d.document}} del {{d.date | date}} ID {{d.serial}}. {{d.description}}</span>
                </template>
                <span class="font-italic text--disabled" v-else>No se registraron referencias.</span>
              </v-list-item-subtitle>
            </v-list-item>
            <template v-if="grant.id">
              <v-list-item>
                <v-list-item-title>Archivo de cesión</v-list-item-title>
                <v-list-item-subtitle>
                  <v-btn :href="grant.file" target="_blank" text color="primary" :ripple="false" height="24">Ir al archivo</v-btn>
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Track ID de la cesión</v-list-item-title>
                <v-list-item-subtitle>{{grant.track_id}}</v-list-item-subtitle>
              </v-list-item>
            </template>
            <v-list-item v-if="[33, 34].includes(instance.document.code)">
              <v-list-item-title>Eventos</v-list-item-title>
              <v-list-item-subtitle>
                <template v-if="supplierresponsesList.length">
                  <span class="d-block" v-for="d in supplierresponsesList" :key="d.id">{{d.code}} {{d.description}} Registrado el {{d.date_time | dateTime}}</span>
                </template>
                <template v-else>
                  <span class="font-italic text--disabled mr-1" >No existen eventos registrados en SII.</span>
                  <v-btn class="mt-n1" @click="builtInSupllierResponses" :loading="$store.state.billers.createLoader" text color="primary" :ripple="false">Actualizar eventos</v-btn>
                </template>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
      <!-- end SII management -->

      <!-- NFE.io management -->
      <v-card class="px-0 my-5" v-else-if="account.country.id == 'BR' && instance.document.has_local_format && !['pending'].includes(instance.status)">
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Gestión NFE.io</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1" v-if="instance.tax_agency.br_status && instance.tax_agency.br_status !== 'None'">
              <v-menu close-on-click close-on-content-click offset-y left :nudge-bottom="10" transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" retain-focus-on-click outlined color="secondary">Opciones</v-btn>
                </template>
                <v-list class="pa-0">
                  <v-list-item class="px-5 py-1" v-if="instance.local_file" :href="instance.local_file" target="_blank" :ripple="false">
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">Ver XML</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="px-0 background">
          <v-row align="center" justify="center" v-if="!instance.tax_agency.br_status || instance.tax_agency.br_status === 'None'">
            <v-btn @click="taxAgencyService('invoices', 'invoices', 'tax_agency_status')" :loading="taxAgencyLoader" color="primary" :ripple="false">Actualizar estado desde NFE.io</v-btn>
          </v-row>
          <v-list class="v-list-form-extended transparent" v-else>
            <v-list-item>
              <v-list-item-title>Estado</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip v-if="instance.tax_agency.br_status === 'None'" color="defaultGrey" text-color="secondary" small>Procesando<v-icon class="ml-1" x-small>mdi-clock</v-icon></v-chip>
                <v-chip v-else-if="instance.tax_agency.br_status === 'Error'" color="red lighten-5" text-color="red" small>Error<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
                <v-chip v-else-if="instance.tax_agency.br_status === 'Created'" color="defaultGrey" text-color="secondary" small>Creado<v-icon class="ml-1" x-small>mdi-clock</v-icon></v-chip>
                <v-chip v-else-if="instance.tax_agency.br_status === 'Issued'" color="lightBlue" text-color="primary" small>Emitida<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                <v-chip v-else-if="instance.tax_agency.br_status === 'Cancelled'" color="defaultGrey" text-color="secondary" small>Cancelada<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="['Created', 'Issued', 'Cancelled'].includes(instance.tax_agency.br_status)">
              <v-list-item-title>Código de verificación</v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="instance.tax_agency.br_check_code">{{instance.tax_agency.br_check_code}}</span>
                <span v-else><v-btn @click="taxAgencyService('invoices', 'invoices', 'tax_agency_status')" :loading="taxAgencyLoader" text color="primary" :ripple="false">Actualizar desde NFE.io</v-btn></span>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="instance.tax_agency.br_status === 'Error'">
              <v-list-item-title>Descripción del estado</v-list-item-title>
              <v-list-item-subtitle>
                <span>{{instance.tax_agency.br_status_detail}}</span>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
      <!-- end NFE.io management -->

      <!-- payments list -->
      <v-card class="pa-0 my-5">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Pagos</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <payment-list :show-pagination="false" :show-sort="false" :show-criteria="{invoice: instance.id}" />
        </v-card-text>
      </v-card>
      <!-- end payments list -->

      <v-card class="pa-0 my-5">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Productos</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th class="text-right">Cantidad</th>
                  <th>Unidad</th>
                  <th class="text-right">Precio</th>
                  <th class="text-center">Exento</th>
                  <th class="text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="d in instance.lines">
                  <tr class="no-hover" :key="d.id">
                    <td>
                      <span class="body-2 text--secondary">{{d.name}}</span>
                      <span class="d-block">{{d.description}}</span>
                    </td>
                    <td class="text-right">
                      <span class="d-block text--secondary">{{d.quantity}}</span>
                      <span class="d-block" v-if="d.code"><template v-if="d.code_scheme">{{d.code_scheme}} - </template>{{d.code}}</span>
                    </td>
                    <td>
                      <span class="d-block">{{d.unit || '-'}}</span>
                    </td>
                    <td class="text-right">
                      <span class="d-block text--secondary font-weight-medium">{{d.amount | number(false)}}</span>
                      <span class="d-block" v-if="d.discount">
                        <template v-if="d.discount_scheme === '%'">- {{d.discount}}%</template>
                        <template v-else>- {{d.discount | currency(instance.currency)}} {{instance.currency}}</template>
                      </span>
                    </td>
                    <td class="text-center">
                      <v-switch class="mt-0 mr-n2 d-inline-block" v-model="d.exempt" disabled dense hide-details />
                    </td>
                    <td class="text-right">
                      <span class="body-2 text--secondary font-weight-medium">{{d.total | currency(instance.currency)}} {{instance.currency}}</span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
          <v-divider />
          <!-- summary -->
          <v-row align="center" justify="end">
            <v-col cols="4">
              <v-list class="pa-0 transparent">
                <v-list-item class="py-0">
                  <v-list-item-title>Subtotal</v-list-item-title>
                  <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{instance.subtotal | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="py-0">
                  <v-list-item-title>Descuento</v-list-item-title>
                  <v-list-item-subtitle class="text-right text--secondary font-weight-medium">
                    <template v-if="instance.discount">
                      {{instance.discount | number}}
                      <template v-if="instance.discount_scheme === '%'">{{instance.discount_scheme}}</template>
                      <template v-if="instance.discount_scheme === '$'">{{instance.currency}}</template>
                    </template>
                    <template v-else>-</template>
                </v-list-item-subtitle>
                </v-list-item>
                <template v-if="instance.document.has_taxes_detail">
                  <v-divider class="ml-4 my-1" />
                  <v-list-item class="py-0">
                    <v-list-item-title>Exento</v-list-item-title>
                    <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{instance.exempt | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                  </v-list-item>
                  <template v-if="instance.document.has_taxes">
                    <v-list-item class="py-0">
                      <v-list-item-title>Neto</v-list-item-title>
                      <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{instance.amount | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                    </v-list-item>
                    <v-divider class="ml-4 my-1" />
                    <v-list-item class="py-0">
                      <v-list-item-title>Impuestos ({{instance.tax_percent}}%)</v-list-item-title>
                      <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{instance.tax | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </template>
                <v-divider class="ml-4 my-1" />
                <v-list-item class="py-0">
                  <v-list-item-title class="body-1 text--secondary font-weight-medium">Total factura</v-list-item-title>
                  <v-list-item-subtitle class="text-right body-1 text--secondary font-weight-medium">{{instance.total | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <template v-if="creditnotesList.length">
            <v-row align="center" justify="end">
              <v-col class="py-0 mt-n2" cols="12">
                <v-list class="pa-0 transparent">
                  <v-divider />
                  <template v-for="(c, i) in creditnotesList">
                    <v-list-item :key="i" class="py-0">
                      <v-list-item-title>
                        <v-btn :to="{name: 'CreditNoteRetrieve', params: {id: c.id}}" height="20" text color="primary" :ripple="false">
                          <span class="d-inline-block">{{c.document.name}} Nº {{c.number}} de {{c.date | date}}</span>
                          <v-icon right small>mdi-arrow-right</v-icon>
                        </v-btn>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{c.total | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                    </v-list-item>
                    <v-divider :key="`${i}-divider`" />
                  </template>
                </v-list>
              </v-col>
              <v-col cols="4" class="pt-2">
                <v-list class="pa-0 transparent">
                  <v-list-item class="py-0">
                    <v-list-item-title>Total créditos</v-list-item-title>
                    <v-list-item-subtitle class="text-right text--secondary font-weight-medium">- {{totalCredits | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
            <template v-if="debitnotesList.length">
              <v-row align="center" justify="end">
                <v-col class="py-0 mt-n2" cols="12">
                  <v-list class="pa-0 transparent">
                    <v-divider />
                    <template v-for="(c, i) in debitnotesList">
                      <v-list-item :key="i" class="py-0">
                        <v-list-item-title>
                          <v-btn :to="{name: 'DebitNoteRetrieve', params: {id: c.id}}" height="20" text color="primary" :ripple="false">
                            <span class="d-inline-block">{{c.document.name}} Nº {{c.number}} de {{c.date | date}}</span>
                            <v-icon right small>mdi-arrow-right</v-icon>
                          </v-btn>
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{c.total | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-divider :key="`${i}-divider`" />
                    </template>
                  </v-list>
                </v-col>
                <v-col cols="4" class="pt-2">
                  <v-list class="pa-0 transparent">
                    <v-list-item class="py-0">
                      <v-list-item-title>Total débitos</v-list-item-title>
                      <v-list-item-subtitle class="text-right text--secondary font-weight-medium">+ {{totalDebits | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </template>
            <v-row align="center" justify="end">
              <v-col cols="4" class="pt-2">
                <v-list class="pa-0 transparent">
                  <v-list-item class="py-0">
                    <v-list-item-title class="text--secondary font-weight-medium">Total factura ajustada</v-list-item-title>
                    <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{totalAdjustedInvoice | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </template>
          <!-- end summary -->
        </v-card-text>
      </v-card>
    </v-col>

    <!-- dialog reminder -->
    <v-dialog v-if="!$store.state.invoices.retrieveLoader" v-model="dialogReminder" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Enviar factura</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="dialogReminder=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-subtitle class="py-2 px-3 background">
          <v-row align="start" no-gutters>
            <v-col cols="9">
              <v-autocomplete class="ma-0" v-model="$v.reminder.recipient.$model" :items="contactsList.concat({ name: 'Email de facturación', email: instance.customer.email })" :error="$v.reminder.recipient.$error" prefix="Para" small-chips deletable-chips :hide-details="!$v.reminder.recipient.$error" required multiple chips item-text="name" item-value="email"
                hint="Selecciona al menos un destinatario" no-data-text="No existen contactos asociados al cliente" persistent-hint autofocus dense outlined color="blue-grey lighten-2" background-color="white">
                <template v-slot:item="data">
                  <template v-if="data.item.orgunit">{{data.item.name}} de {{data.item.orgunit.name}} ({{data.item.email}})</template>
                  <template v-else>{{data.item.name}} ({{data.item.email}})</template>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="3" class="text-right">
              <v-menu close-on-click close-on-content-click offset-y left :nudge-bottom="10" transition="slide-y-transition">
                <template v-slot:activator="{on, attrs}">
                  <v-btn class="ml-2" v-bind="attrs" v-on="on" height="40" outlined color="secondary" :ripple="false" retain-focus-on-click>Elegir una plantilla</v-btn>
                </template>
                <v-list class="pa-0">
                  <template v-for="(d, i) in templatesList">
                    <v-list-item class="px-5 py-1" @click="setReminderMessage(i)" :key="i" :ripple="false">
                      <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">{{d.name}}</v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-divider v-if="templatesList.length" />
                  <v-list-item class="px-5 py-1" :to="{name: 'AccountSettingsInvoicing'}" target="_blank" :ripple="false">
                    <v-list-item-title class="subtitle-2 text--secondary font-weight-medium">Crear una plantilla<v-icon small right>mdi-open-in-new</v-icon></v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
          <v-row align="center" no-gutters>
            <v-col cols="9">
              <v-text-field class="mx-0" prefix="Asunto" v-model="$v.reminder.subject.$model" :error="$v.reminder.subject.$error" background-color="white" outlined required single-line dense maxlength="60" hint="Escribe un asunto para el mensaje" persistent-hint :hide-details="!$v.reminder.subject.$error" />
            </v-col>
            <v-col cols="3">
              <v-tooltip v-if="instance.payments[0] && (!instance.payments[0].enable_checkout || !['requires_payment_method', 'failed'].includes(instance.payments[0].status))" bottom max-width="280px" transition="scale-transition">
                <template v-slot:activator="{on}">
                  <span class="d-block" v-on="on">
                    <v-switch class="ma-0 ml-3" :width="32" :height="18" label="Botón de pago" disabled hide-details />
                  </span>
                </template>
                <span v-if="!['requires_payment_method', 'failed'].includes(instance.payments[0].status)">El pago de la factura está pagado o cancelado.</span>
                <span v-else-if="!instance.payments[0].enable_checkout">La página de pago no está habilitada para el pago de esta factura.</span>
              </v-tooltip>
              <template v-else>
                <v-switch class="ma-0 ml-3" v-model="reminder.action" value="pay" :width="32" :height="18" label="Botón de pago" hide-details />
              </template>
            </v-col>
          </v-row>
        </v-card-subtitle>
        <v-divider />
        <v-card-text>
          <v-row class="mx-0 py-5" align="center" justify="center">
            <v-col cols="10">
              <reminder-invoice-email :instance="instance" :template="account.email_template" :action="reminder.action" :color-background="account.color_background" @update:message="reminder.message=$event" :message.sync="reminder.message" />
            </v-col>
          </v-row>
          <v-row class="mx-0 pb-2" justify="center">
            <v-col cols="10">
              <span class="mr-2 body-2 text--secondary">Archivos adjuntos</span>
              <v-chip class="ma-1" color="secondary" small outlined><v-icon left small>mdi-paperclip</v-icon>{{instance.number}}.pdf</v-chip>
              <v-chip class="ma-1" v-if="instance.local_file" color="secondary" small outlined><v-icon left small>mdi-paperclip</v-icon>{{instance.number}}.xml</v-chip>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="dialogReminder=false" outlined>Cancelar</v-btn>
          <v-btn @click="createReminder" :loading="$store.state.reminders.createLoader" color="primary">Enviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog reminder -->

    <!-- dialog pdf -->
    <v-dialog v-model="modalRender" v-if="instance.id" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">{{`PDF ${instance.document.name} Nº ${instance.number}`}}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn @click="modalRender=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-row v-if="loaderRender">
            <v-col cols="12" class="py-0 ma-0">
              <v-skeleton-loader class="rounded-0" transition="fade-transition" type="table" />
            </v-col>
          </v-row>
          <v-row v-else-if="!loaderRender && !renderUrl">
            <v-col cols="12" class="py-16 text-center">
              <v-icon class="d-block" color="secondary" size="70">mdi-image-broken-variant</v-icon>
              <span class="d-block subtitle-1">PDF no disponible</span>
            </v-col>
          </v-row>
          <div id="render"></div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn :href="renderUrl" :download="`${instance.document.id}_N${instance.number}`" color="primary" dark>Descargar PDF</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog pdf -->

    <!-- dialog credit note -->
    <template v-if="!$store.state.invoices.retrieveLoader">
      <credit-note-create :show-create="dialogCreditNote" @update:dialog="dialogCreditNote=$event" @update:create="retrieve" />
      <credit-note-total-create :show-create="dialogCreditNoteTotal" @update:dialog="dialogCreditNoteTotal=$event" @update:create="retrieve" />
      <invoice-cancellation-create :show-create="dialogCancellation" @update:dialog="dialogCancellation=$event" @update:create="retrieve" />
    </template>
    <!-- end dialog credit note -->

    <!-- dialog grant -->
    <v-dialog v-model="dialogGrant" width="700" persistent no-click-animation scrollable>
      <v-card class="pa-0">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Crear cesión de la factura</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="ml-3" @click="dialogGrant=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-row class="py-6">
            <v-col md="10">
              <v-list class="v-list-form transparent">
                <v-list-item class="pt-2">
                  <v-list-item-title>
                    <span class="text--secondary font-weight-medium">Cesionario</span>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item class="py-2">
                  <v-list-item-title>RUT</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-col cols="8">
                      <v-text-field v-model="$v.grant.tax_id.$model" :error="$v.grant.tax_id.$error" v-dni="'CL'" background-color="white" class="mt-3" outlined hide-details required single-line dense maxlength="32" />
                    </v-col>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="py-2">
                  <v-list-item-title>Nombre</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-text-field v-model="$v.grant.name.$model" :error="$v.grant.name.$error" background-color="white" class="mt-3" outlined hide-details required single-line dense maxlength="32" />
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="py-2">
                  <v-list-item-title>Email</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-text-field v-model="$v.grant.email.$model" :error="$v.grant.email.$error" background-color="white" class="mt-3" outlined hide-details required single-line dense maxlength="56" />
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="py-2">
                  <v-list-item-title>Dirección</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-text-field v-model="$v.grant.address.$model" :error="$v.grant.address.$error" background-color="white" class="mt-3" outlined hide-details required single-line dense maxlength="36" />
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="mt-6">
                  <v-list-item-title>
                    <span class="text--secondary font-weight-medium">Cedente</span>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item class="pb-2">
                  <v-list-item-title>Email</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-text-field v-model="$v.grant.assignor_email.$model" :error="$v.grant.assignor_email.$error" background-color="white" class="mt-3" outlined hide-details required single-line dense maxlength="36" />
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="dialogGrant=false" outlined color="secondary">Cancelar</v-btn>
          <v-btn @click="createGrant" :loading="$store.state.billers.createLoader" color="primary">Crear cesión</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog grant -->
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { email, minLength, maxLength, required } from 'vuelidate/lib/validators'
import { rutValidator } from 'vue-dni'
import { invoiceTypes } from '@/collections/documents'
import numeral from 'numeral'
import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'

import CreditNoteCreate from '@/components/invoice/CreditNoteCreate'
import CreditNoteTotalCreate from '@/components/invoice/CreditNoteTotalCreate'
import InvoiceCancellationCreate from '@/components/invoice/InvoiceCancellationCreate'
import PaymentList from '@/components/payments/PaymentList'
import ReminderInvoiceEmail from '@/components/reminders/invoices/Email'

import GenericViewMixin from '@/mixins/GenericViewMixin'
import RenderViewMixin from '@/mixins/RenderViewMixin'
import TaxAgencyViewMixin from '@/mixins/TaxAgencyViewMixin'

export default {
  inject: ['theme'],
  mixins: [
    GenericViewMixin,
    RenderViewMixin,
    TaxAgencyViewMixin
  ],
  components: {
    CreditNoteCreate,
    CreditNoteTotalCreate,
    InvoiceCancellationCreate,
    PaymentList,
    ReminderInvoiceEmail,
    VSkeletonTemplate: {
      functional: true,
      render (h, { data, props, children }) {
        return h('v-skeleton-loader', {
          ...data,
          props: {
            boilerplate: true,
            elevation: 2,
            ...props
          }
        }, children)
      }
    }
  },
  data: () => ({
    grant: {
      tax_id: '',
      name: '',
      address: '',
      email: '',
      assignor_email: ''
    },
    dialogGrant: false,
    dialogCreditNote: false,
    dialogCreditNoteTotal: false,
    dialogCancellation: false,
    dialogReminder: false,
    invoiceType: null,
    loadingInvoiceDuplicate: false,
    reminder: {
      action: 'pay',
      message: '',
      recipient: '',
      subject: '',
      template: ''
    }
  }),
  computed: {
    ...mapState({
      instance: state => state.invoices.invoicesDetail,
      supplierresponsesList: state => state.billers.supplierresponsesList,
      creditnotesList: state => state.invoices.creditnotesList,
      debitnotesList: state => state.invoices.debitnotesList,
      messagesList: state => state.reminders.messagesList,
      templatesList: state => state.reminders.templatesList,
      contactsList: state => state.customers.contactsList,
      paymentsList: state => state.payments.paymentsList
    }),
    totalCredits () {
      return this.sumProperty(this.creditnotesList, 'total')
    },
    totalDebits () {
      return this.sumProperty(this.debitnotesList, 'total')
    },
    totalAdjustedInvoice () {
      return this.instance.total - this.totalCredits + this.totalDebits
    },
    totalPaid () {
      return this.instance.payments.length ? this.instance.payments.map(c => c.amount_paid).reduce((a, b) => a + b) : 0
    }
  },
  created () {
    this.retrieve()
  },
  methods: {
    retrieve () {
      this.$store.dispatch('invoices/RETRIEVE', {
        resource: 'invoices',
        id: this.$route.params.id
      })
      .then(() => {
        if (this.instance.status === 'draft') {
          this.$router.push({
            name: 'InvoiceUpdate',
            params: {
              id: this.$route.params.id
            }
          })
        }
        this.$store.dispatch('invoices/LIST', {
          resource: 'creditnotes',
          query: {
            expand: 'document',
            invoice: this.instance.id
          },
          loader: false
        })
        this.$store.dispatch('invoices/LIST', {
          resource: 'debitnotes',
          query: {
            expand: 'document',
            credit_note__invoice: this.instance.id
          },
          loader: false
        })
        this.$store.dispatch('payments/LIST', {
          resource: 'payments',
          query: {
            invoice: this.instance.id,
            ordering: 'date'
          }
        })
        this.$store.dispatch('reminders/LIST', {
          resource: 'reminders/messages',
          query: {
            invoice: this.instance.id
          }
        })
        this.$store.dispatch('reminders/LIST', {
          resource: 'reminders/templates',
          query: {
            kind: 'invoicing'
          }
        })
        this.$store.dispatch('customers/LIST', {
          resource: 'contacts',
          query: {
            customer: this.instance.customer.id,
            expand: 'orgunit'
          }
        })
        if (this.instance.document.country === 'CL') {
          this.$store.dispatch('billers/LIST', {
            resource: 'billers/built_in/grants',
            query: {
              invoice: this.instance.id
            }
          })
          .then((response) => {
            if (response.data.results.length) {
              this.grant = response.data.results[0]
            }
          })
          if ([33, 34].includes(this.instance.document.code)) {
            this.$store.dispatch('billers/LIST', {
              resource: 'billers/built_in/supplierresponses',
              query: {
                invoice: this.instance.id
              }
            })
          }
        }
        this.reminder.message = `Hola ${this.instance.customer.name},\n\n`
        this.reminder.message += `adjunto ${this.instance.document.name} N° ${this.instance.number}, `
        this.reminder.message += `correspondiente al ${moment(this.instance.date).format('DD MMM YYYY')} `
        this.reminder.message += `por un monto de ${numeral(this.instance.total).format('0,0')}\n\nAtte. ${this.account.name}`
      })
      .then(() => {
        this.invoiceType = invoiceTypes.find(d => d.id === this.instance.document.id)
      })
      .then(() => {
        const payment = this.instance.payments[0]
        if (payment && (!payment.enable_checkout || !['requires_payment_method', 'failed'].includes(payment.status))) {
          this.reminder.action = null
        }
      })
    },
    createCreditNote () {
      this.$v.creditNote.$touch()
      if (this.$v.creditNote.$invalid) {
        return false
      }
      this.$store.dispatch('invoices/CREATE', {
        resource: 'creditnotes',
        payload: {
          invoice: this.instance.id,
          note: this.creditNote.note
        }
      })
      .then(() => {
        this.dialogCreditNote = false
        this.retrieve()
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    createReminder () {
      this.reminder.invoice = this.instance.id
      this.$v.reminder.$touch()
      if (this.$v.reminder.$invalid) {
        return false
      }
      this.$store.dispatch('reminders/CREATE', {
        resource: 'reminders/messages',
        payload: this.reminder
      })
      .then(() => {
        this.dialogReminder = false
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    setReminderMessage (i) {
      const template = Object.assign({}, this.templatesList[i])
      const tokens = [
        { name: 'number', type: String },
        { name: 'document.name', type: String },
        { name: 'date', type: Date },
        { name: 'due_date', type: Date },
        { name: 'total', type: Number, currency: true },
        { name: 'customer.name', type: String }
      ]
      tokens.forEach(token => {
        let property = null
        const attr = token.name.split('.')
        if (attr[1]) {
          property = this.instance[attr[0]][attr[1]]
        } else {
          property = this.instance[attr[0]]
        }
        if (property) {
          if (token.type === Date) {
            property = this.$options.filters.date(property)
          }
          if (token.type === Number) {
            if (token.currency) {
              property = this.$options.filters.currency(property)
            }
          }
          template.message = template.message.split(`{{${token.name}}}`).join(property)
        }
      })
      this.reminder = Object.assign(this.reminder, {
        template: template.name,
        subject: template.subject,
        message: template.message
      })
    },
    remove (item) {
      const index = this.$v.reminder.recipient.$model.indexOf(item.name)
      if (index >= 0) {
        this.$v.reminder.recipient.$model.splice(index, 1)
      }
    },
    createGrant () {
      this.$v.grant.$touch()
      if (this.$v.grant.$invalid) {
        return false
      }
      this.grant.invoice = this.instance.id
      this.$store.dispatch('billers/CREATE', {
        resource: 'billers/built_in/grants',
        payload: this.grant
      })
      .then((response) => {
        this.grant = response.data
        this.dialogGrant = false
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    cloneInvoice () {
      const dueDate = moment(this.today).add(30, 'days').format('YYYY-MM-DD')
      const payload = cloneDeep(this.instance)
      delete payload.id
      delete payload.imported
      delete payload.local_file
      delete payload.metadata
      delete payload.number
      delete payload.status
      delete payload.subscription
      delete payload.tax_agency
      delete payload.test_mode
      delete payload.created
      delete payload.updated
      payload.collection_method = 'deferred'
      payload.customer = this.instance.customer.id
      payload.date = this.today
      payload.document = this.instance.document.id
      payload.due_date = dueDate
      payload.lines.map((line) => {
        Object.keys(line).forEach((key) => {
          if (['created', 'id', 'invoice', 'updated', 'total', 'total_adjusted'].includes(key)) {
            delete line[key]
          }
          if (!line.discount) {
            line.discount = 0
            line.discount_scheme = '%'
          }
        })
        return line
      })
      payload.payments = [{
        amount: this.instance.total,
        date: dueDate,
        description: 'Pago de factura',
        currency: this.instance.currency
      }]
      this.loadingInvoiceDuplicate = true
      this.$store.dispatch('invoices/CREATE', {
        resource: 'invoices',
        payload: payload
      })
      .then((response) => {
        this.$router.push(`/invoices/${response.data.id}/edit`)
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.loadingInvoiceDuplicate = false
      })
    },
    builtInSupllierResponses () {
      this.$store.dispatch('billers/CREATE', {
        resource: 'billers/built_in/supplierresponses',
        payload: {
          invoice: this.instance.id
        }
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    updateCollectible () {
      let dialogTitle = 'Marcar como incobrable'
      let dialogMsg = 'Esta factura no será considerada como pendiente de pago, pero será posible pagarla si el cliente lo intenta.'
      let successMsg = 'La factura se marcó como incobrable'

      if (this.instance.status === 'uncollectible') {
        dialogTitle = 'Marcar como cobrable'
        dialogMsg = 'Esta factura volverá a ser considerada como pendiente de pago.'
        successMsg = 'La factura se marcó como cobrable'
      }

      this.$dialog.warning({
        title: dialogTitle,
        text: `
          <span class="d-block mb-4">${dialogMsg}</span>
          <span class="d-block">¿Estás seguro de continuar?</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('invoices/RETRIEVE', {
                resource: 'invoices',
                id: this.$route.params.id,
                detail: 'uncollectible',
                loader: false
              })
              .then(() => {
                this.$dialog.message.info(successMsg)
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    }
  },
  validations: {
    reminder: {
      recipient: {
        required,
        maxLength: maxLength(8)
      },
      subject: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(60)
      },
      message: {
        required
      }
    },
    grant: {
      tax_id: {
        required,
        maxLength: maxLength(32),
        rutValidator
      },
      name: {
        required,
        maxLength: maxLength(32)
      },
      address: {
        required,
        maxLength: maxLength(56)
      },
      email: {
        required,
        email,
        maxLength: maxLength(36)
      },
      assignor_email: {
        required,
        email,
        maxLength: maxLength(36)
      }
    }
  }
}
</script>